import { formatRupeeLabel } from '../../../../../display-values/lumpsumPlanAmount'
import type { LumpsumAllocationExecutedRecommendation } from '../../../../api/allocation.types'
import { LumpsumRecommendationActionType } from '../../../../api/common.types'
import { AllocationMovementToggle, PlanCategoryBucketKey } from '../../../../api/types'
import { useOpenPlanRecommendationBottomSheet } from '../../../../plan-bottom-sheet/open/useOpenPlanRecommendationBottomSheet'
import { useLumpsumPlansContext } from '../../../LumpsumPlansProvider'
import { AllocationFundCardTrailingLayout } from '../../../lumpsum-ui-components/AllocationFundCardTrailingLayout'
import { PlansFundCard, type PlanFundCardCta, type PlanFundCardInsight } from '../../../lumpsum-ui-components/PlansFundCard'
import { buildAllocationBuyInsights } from './AllocationBuyFundCard'
import { planPercentageLabel } from '../../../../../display-values/lumpsumPlanPercentage'
import {
  ALLOCATION_SELL_FUND_CARD_PIE_ICON_SVG,
  buildAllocationSellInsights
} from './AllocationSellFundCard'
import { PlansFundRole, PlansScreen } from '../../../../../analytics/plansEvents'
import { trackPlansFundCardCta, trackPlansFundCardOpen } from '../../../../../analytics/plansTrack'

type AllocationExecutedFundCardProps = {
  recommendation: LumpsumAllocationExecutedRecommendation
  categoryName: string
  showBottomDivider?: boolean
}

export function AllocationExecutedFundCard({
  recommendation,
  categoryName,
  showBottomDivider = true
}: AllocationExecutedFundCardProps) {
  const { openPlanRecommendationBottomSheet } = useOpenPlanRecommendationBottomSheet()
  const isin = recommendation?.isin ?? ''
  const fundName = recommendation?.fundName ?? ''
  const screenName = PlansScreen.LumpsumAllocation
  const fundRole = PlansFundRole.Executed
  const actionType = recommendation?.actionType ?? null
  const isBuy = actionType === LumpsumRecommendationActionType.BUY
  const actionLabel = isBuy ? 'Buy' : 'Sell'
  const completedLabel = `${actionLabel} completed`

  const openPlanBottomSheet = () => {
    openPlanRecommendationBottomSheet({
      isin,
      bucketType: PlanCategoryBucketKey.executedRecommendations,
      recommendationAction: actionType ?? undefined
    })
  }

  const handleCardClick = () => {
    trackPlansFundCardOpen({ screenName, fundRole, fundName, fundIsin: isin })
    openPlanBottomSheet()
  }

  const ctas: PlanFundCardCta[] = [
    {
      id: `${isin}-action`,
      label: completedLabel,
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: completedLabel, fundName, fundIsin: isin })
        console.log(completedLabel, isin)
      },
      variant: 'completed'
    },
    {
      id: `${isin}-view-plan`,
      label: 'View plan',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'View plan', fundName, fundIsin: isin })
        openPlanBottomSheet()
      },
      variant: 'tertiary'
    }
  ]

  return (
    <PlansFundCard
      fund={{
        isin,
        logoUrl: recommendation?.mfLogoUrl ?? '',
        title: fundName,
        fundNature: recommendation?.fundNature ?? '',
        category: recommendation?.fundCategory ?? '',
        purchaseMode: recommendation?.purchaseMode ?? ''
      }}
      topRight={<AllocationExecutedFundCardTrailing recommendation={recommendation} />}
      status={{
        rank: recommendation?.powerRank ?? 0,
        rating: recommendation?.powerRating ?? ''
      }}
      ctas={ctas}
      insights={buildAllocationExecutedInsights(recommendation, categoryName)}
      showBottomDivider={showBottomDivider}
      onCardClick={handleCardClick}
    />
  )
}

export function buildAllocationExecutedInsights(
  recommendation: LumpsumAllocationExecutedRecommendation,
  categoryName: string
): PlanFundCardInsight[] {
  if (recommendation?.actionType === LumpsumRecommendationActionType.BUY) {
    return buildAllocationBuyInsights(recommendation, categoryName)
  }

  return buildAllocationSellInsights(recommendation, categoryName)
}

export function AllocationExecutedFundCardTrailing({
  recommendation
}: {
  recommendation: LumpsumAllocationExecutedRecommendation
}) {
  const { allocationMovementToggle } = useLumpsumPlansContext()

  if (allocationMovementToggle === AllocationMovementToggle.FUND_MOVEMENT) {
    return <AllocationExecutedFundMovementTrailing recommendation={recommendation} />
  }

  return <AllocationExecutedAllocationMovementTrailing recommendation={recommendation} />
}

export function AllocationExecutedFundMovementTrailing({
  recommendation
}: {
  recommendation: LumpsumAllocationExecutedRecommendation
}) {
  return (
    <AllocationFundCardTrailingLayout
      primaryLabel={`${formatRupeeLabel({ rawAmount: recommendation?.currentValue ?? 0 })}`}
      secondaryIconHtml={ALLOCATION_EXECUTED_FUND_CARD_COMPLETED_TICK_ICON_SVG}
      secondaryText="Completed"
    />
  )
}

export function AllocationExecutedAllocationMovementTrailing({
  recommendation
}: {
  recommendation: LumpsumAllocationExecutedRecommendation
}) {
  return (
    <AllocationFundCardTrailingLayout
      primaryLabel={`${formatRupeeLabel({ rawAmount: recommendation?.currentValue ?? 0 })}`}
      secondaryIconHtml={ALLOCATION_SELL_FUND_CARD_PIE_ICON_SVG}
      secondaryText={planPercentageLabel({
        rawPercentage:
          recommendation?.currentAllocationPercentage ?? 0
      })}
    />
  )
}

export const ALLOCATION_EXECUTED_FUND_CARD_COMPLETED_TICK_ICON_SVG = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M3.99562 11C3.74385 11.0004 3.49508 10.9436 3.2671 10.8337C3.03911 10.7237 2.83752 10.5633 2.67673 10.3638L1.17114 8.39362C1.10767 8.31441 1.06021 8.22294 1.03156 8.12459C1.00291 8.02624 0.99363 7.92299 1.00428 7.82089C1.01492 7.7188 1.04528 7.61992 1.09356 7.53007C1.14184 7.44022 1.20707 7.36121 1.28543 7.29767C1.36378 7.23413 1.45368 7.18734 1.54984 7.16006C1.646 7.13278 1.74649 7.12555 1.84541 7.13879C1.94432 7.15204 2.03967 7.1855 2.12585 7.2372C2.21203 7.2889 2.2873 7.35781 2.34726 7.43987L3.84486 9.3926C3.86258 9.41756 3.88678 9.43688 3.91468 9.44834C3.94259 9.45979 3.97306 9.46291 4.00261 9.45734C4.03524 9.45736 4.06745 9.44979 4.09684 9.4352C4.12623 9.4206 4.15204 9.39937 4.17234 9.37307L9.66056 2.29701C9.72147 2.21655 9.79728 2.14934 9.8836 2.09927C9.96993 2.0492 10.065 2.01726 10.1634 2.00531C10.2619 1.99336 10.3616 2.00163 10.4569 2.02964C10.5521 2.05766 10.6411 2.10486 10.7185 2.16851C10.7959 2.23216 10.8603 2.311 10.9079 2.40045C10.9555 2.4899 10.9854 2.58819 10.9958 2.68962C11.0063 2.79105 10.997 2.89361 10.9687 2.99134C10.9404 3.08908 10.8934 3.18005 10.8307 3.25898L5.33248 10.3422C5.17269 10.5465 4.97068 10.7115 4.74116 10.8253C4.51163 10.9391 4.26035 10.9987 4.0056 11H3.99562Z" fill="#3D3D3D"/>
</svg>`
